/**
 * 应用管理模块 API 接口
 * 提供应用的 CRUD、API Key 管理等接口
 */

import { get, post, del, patch } from '../client';
import type { ApiResult } from '../types/common';
import type {
  AppDetail,
  AppListResult,
  CreateAppRequest,
  CreateAppResult,
  AppChallengeResult,
  ResetKeyRequest,
  ResetKeyResult,
  DeleteAppRequest,
  AppPushConfigUpdateRequest,
  AppPushConfigUpdateResult,
  UpdateAppStatusRequest,
  UpdateAppStatusResult,
  RpcChallengeRequest,
  AppRpcChallengeResult,
  EnableAppRpcRequest,
  RotateAppRpcRequest,
  AppRpcCredentialsResult,
  GetAppRpcEndpointsParams,
} from './apps';

// ==================== 应用 CRUD ====================

/**
 * 获取当前用户的应用列表
 * 
 * 返回当前登录用户创建的所有应用列表（仅包含启用状态的应用）。
 * 
 * @returns 应用列表
 * @requiresAuth 需要 Bearer Token 认证
 * 
 * @example
 * ```ts
 * const result = await getAppList();
 * if (result.ok) {
 *   const apps = result.data.apps;
 *   console.log('应用数量:', apps.length);
 * }
 * ```
 */
export async function getAppList(): Promise<ApiResult<AppListResult>> {
  return get<AppListResult>('/apps', { requiresAuth: true });
}

/**
 * 创建新应用
 * 
 * 创建新应用并生成 API Key。
 * **重要**：响应中的 `key` 字段（完整明文 API Key）**仅在创建时返回一次**，
 * 后续无法再次获取完整密钥，请立即保存。若丢失，需通过重置接口更换。
 * 
 * @param data - 创建请求参数
 * @param data.name - 应用名称，长度 1-100 个字符
 * @returns 创建结果，包含应用信息和完整的 API Key
 * @requiresAuth 需要 Bearer Token 认证
 * 
 * @example
 * ```ts
 * const result = await createApp({ name: '我的 DApp' });
 * if (result.ok) {
 *   const { id, name, key, keyPrefix } = result.data;
 *   // ⚠️ 立即保存 key，之后无法再获取
 *   localStorage.setItem('api_key', key);
 * }
 * ```
 */
export async function createApp(
  data: CreateAppRequest
): Promise<ApiResult<CreateAppResult>> {
  return post<CreateAppResult>('/apps', data, { requiresAuth: true });
}

/**
 * 获取应用详情
 *
 * 对齐 OpenAPI: GET /apps/{id}，返回 {@link AppDetail}
 *
 * 返回内容比列表项 `AppInfo` 多一组 `stats30d`（最近 30 天调用/扣费统计），
 * 适合用于 KeyDetails 页面头部信息展示。
 *
 * @param appId - 应用公开标识（格式 `app_xxx`）
 * @returns 应用详情（含 stats30d）
 * @requiresAuth 需要 Bearer Token 认证
 *
 * @example
 * ```ts
 * const result = await getAppDetail('app_Kb3mXd9');
 * if (result.ok) {
 *   const app = result.data;
 *   console.log('应用名称:', app.name);
 *   console.log('30 天调用次数:', app.stats30d.totalCalls30d);
 *   console.log('30 天积分消耗:', app.stats30d.creditsCharged30d);
 * }
 * ```
 */
export async function getAppDetail(appId: string): Promise<ApiResult<AppDetail>> {
  return get<AppDetail>(`/apps/${appId}`, { requiresAuth: true });
}

/**
 * 删除应用
 * 
 * 删除指定应用（软删除，状态置为禁用，对应 API Key 立即失效）。
 * 
 * **流程**：
 * 1. 先调用 `getAppChallenge()` 获取挑战消息
 * 2. 使用钱包私钥对挑战消息签名
 * 3. 携带签名调用本接口
 * 
 * @param appId - 应用唯一 ID
 * @param data - 删除请求参数
 * @param data.signature - 使用钱包私钥对挑战消息签名后的十六进制字符串
 * @returns 删除结果
 * @requiresAuth 需要 Bearer Token 认证
 * 
 * @example
 * ```ts
 * // 1. 获取挑战
 * const challenge = await getAppChallenge();
 * if (!challenge.ok) return;
 * 
 * // 2. 使用钱包签名
 * const signature = await wallet.signMessage(challenge.data.message);
 * 
 * // 3. 删除应用
 * const result = await deleteApp('app_Kb3mXd9', { signature });
 * if (result.ok) {
 *   console.log('应用已删除');
 * }
 * ```
 */
export async function deleteApp(
  appId: string,
  data: DeleteAppRequest
): Promise<ApiResult<void>> {
  return del<void>(`/apps/${appId}`, {
    requiresAuth: true,
    body: JSON.stringify(data),
  });
}

// ==================== API Key 管理 ====================

/**
 * 获取敏感操作挑战
 * 
 * 执行重置 API Key 或删除应用等敏感操作前，需先获取签名挑战。
 * 
 * **流程**：
 * 1. 调用本接口获取 `nonce` 和 `message`
 * 2. 使用钱包私钥对 `message` 签名
 * 3. 携带签名调用 `resetApiKey()` 或 `deleteApp()`
 * 
 * @returns 挑战结果，包含 nonce 和需要签名的消息
 * @requiresAuth 需要 Bearer Token 认证
 * 
 * @example
 * ```ts
 * const result = await getAppChallenge();
 * if (result.ok) {
 *   const { nonce, message } = result.data;
 *   // 使用钱包签名 message
 * }
 * ```
 */
export async function getAppChallenge(): Promise<ApiResult<AppChallengeResult>> {
  return post<AppChallengeResult>('/apps/challenge', undefined, {
    requiresAuth: true,
  });
}

/**
 * 重置应用 API Key
 * 
 * 重置指定应用的 API Key，旧 Key 立即失效。
 * 
 * **重要**：响应中的 `key` 字段（完整明文 API Key）**仅在重置时返回一次**，
 * 请立即保存。
 * 
 * **流程**：
 * 1. 先调用 `getAppChallenge()` 获取挑战消息
 * 2. 使用钱包私钥对挑战消息签名
 * 3. 携带签名调用本接口
 * 
 * @param appId - 应用唯一 ID
 * @param data - 重置请求参数
 * @param data.signature - 使用钱包私钥对挑战消息签名后的十六进制字符串
 * @returns 重置结果，包含新的 API Key
 * @requiresAuth 需要 Bearer Token 认证
 * 
 * @example
 * ```ts
 * // 1. 获取挑战
 * const challenge = await getAppChallenge();
 * if (!challenge.ok) return;
 * 
 * // 2. 使用钱包签名
 * const signature = await wallet.signMessage(challenge.data.message);
 * 
 * // 3. 重置 Key
 * const result = await resetApiKey('app_Kb3mXd9', { signature });
 * if (result.ok) {
 *   const { key, keyPrefix } = result.data;
 *   // ⚠️ 立即保存新的 key
 *   localStorage.setItem('api_key', key);
 * }
 * ```
 */
export async function resetApiKey(
  appId: string,
  data: ResetKeyRequest
): Promise<ApiResult<ResetKeyResult>> {
  return post<ResetKeyResult>(`/apps/${appId}/reset-key`, data, {
    requiresAuth: true,
  });
}

/**
 * 更新应用状态（启用/暂停/软删除）
 *
 * 对齐 OpenAPI: PATCH /apps/{id}/status（AppStatusRequest）
 *
 * 与 `deleteApp` / `resetApiKey` 相同，此接口需要钱包签名：
 * 1. 先调用 {@link getAppChallenge} 获取 message
 * 2. 用户钱包 `personal_sign` 签名
 * 3. 携带 `{ status, signature }` 调用本接口
 *
 * - `status=0`：暂停应用，API Key 立即失效（清除 KV 缓存）
 * - `status=1`：启用应用，API Key 恢复正常
 * - `status=2`：软删除（建议优先使用 `DELETE /apps/{id}`）
 *
 * @param appId - 应用公开标识（格式 `app_xxx`）
 * @param data - `{ status, signature }`
 * @returns 更新结果 `{ updated: true }`
 * @requiresAuth 需要 Bearer Token 认证
 *
 * @example
 * ```ts
 * const challenge = await getAppChallenge();
 * if (!challenge.ok) return;
 * const signature = await wallet.signMessage(challenge.data.message);
 *
 * // 禁用
 * await updateAppStatus('app_Kb3mXd9', {
 *   status: AppStatus.DISABLED,
 *   signature,
 * });
 * ```
 */
export async function updateAppStatus(
  appId: string,
  data: UpdateAppStatusRequest
): Promise<ApiResult<UpdateAppStatusResult>> {
  return patch<UpdateAppStatusResult>(`/apps/${appId}/status`, data, {
    requiresAuth: true,
  });
}

/**
 * 修改应用交易推送配置
 *
 * 对齐 OpenAPI: PATCH /apps/{id}/config（AppPushConfigUpdateRequest）。
 *
 * 修改应用在 tx-history-service/chain-listen 中的 WebSocket / Webhook 推送配置。
 * 调用前需要先通过 {@link getAppChallenge} 获取 message，并让用户钱包
 * personal_sign 后携带 signature 调用。
 *
 * @param appId - 应用公开标识（格式 `app_xxx`）
 * @param data - 推送配置更新请求体，必须包含 signature，并至少传入一个配置字段
 * @returns 更新结果 `{ updated: true }`
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function updateAppPushConfig(
  appId: string,
  data: AppPushConfigUpdateRequest
): Promise<ApiResult<AppPushConfigUpdateResult>> {
  return patch<AppPushConfigUpdateResult>(`/apps/${appId}/config`, data, {
    requiresAuth: true,
  });
}

// ==================== App RPC / WSS ====================

/**
 * 获取 RPC 操作签名挑战
 *
 * 对齐 YApi 953: POST /apps/{appId}/rpc/challenge
 *
 * - `action=enable`：用于启用 RPC（省略时服务端按 enable）
 * - `action=rotate`：用于轮换共享 Token
 *
 * @param appId - 应用 ID
 * @param data - 可选 `{ action }`
 * @returns `{ nonce, message }`，对 message 做 personal_sign
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getAppRpcChallenge(
  appId: string,
  data?: RpcChallengeRequest
): Promise<ApiResult<AppRpcChallengeResult>> {
  return post<AppRpcChallengeResult>(
    `/apps/${appId}/rpc/challenge`,
    data ?? {},
    { requiresAuth: true }
  );
}

/**
 * 启用 App RPC / WSS
 *
 * 对齐 YApi 953: POST /apps/{appId}/rpc/enable
 *
 * 幂等。生产环境需先 {@link getAppRpcChallenge}（action=enable）并携带 signature。
 * 返回共享 Token 与动态网络列表（敏感字段勿 persist）。
 *
 * @param appId - 应用 ID
 * @param data - `{ signature? }`
 * @returns RpcCredentials
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function enableAppRpc(
  appId: string,
  data: EnableAppRpcRequest = {}
): Promise<ApiResult<AppRpcCredentialsResult>> {
  return post<AppRpcCredentialsResult>(`/apps/${appId}/rpc/enable`, data, {
    requiresAuth: true,
  });
}

/**
 * 查询 App RPC / WSS 凭据与网络列表
 *
 * 对齐 YApi 953: GET /apps/{appId}/rpc/endpoints
 *
 * @param appId - 应用 ID
 * @param params - 可选 `search`（按 network / displayName 模糊匹配）
 * @returns RpcCredentials
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getAppRpcEndpoints(
  appId: string,
  params?: GetAppRpcEndpointsParams
): Promise<ApiResult<AppRpcCredentialsResult>> {
  const search = new URLSearchParams();
  const keyword = params?.search?.trim();
  if (keyword) search.set('search', keyword);
  const qs = search.toString();
  return get<AppRpcCredentialsResult>(
    `/apps/${appId}/rpc/endpoints${qs ? `?${qs}` : ''}`,
    { requiresAuth: true }
  );
}

/**
 * 轮换 App 共享 RPC Token
 *
 * 对齐 YApi 953: POST /apps/{appId}/rpc/rotate
 *
 * 调用前需 {@link getAppRpcChallenge}（action=rotate）并签名。
 * 旧 Token 对后续 HTTP / 新 WSS 立即失效。
 *
 * @param appId - 应用 ID
 * @param data - `{ signature }`
 * @returns 新的 RpcCredentials
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function rotateAppRpcEndpoint(
  appId: string,
  data: RotateAppRpcRequest
): Promise<ApiResult<AppRpcCredentialsResult>> {
  return post<AppRpcCredentialsResult>(`/apps/${appId}/rpc/rotate`, data, {
    requiresAuth: true,
  });
}
